import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './ComparisonWidget.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function ComparisonWidget({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    console.log('🔍 ComparisonWidget rendered:', { 
        id: data.id, 
        type: data.type, 
        isSelected, 
        isEditMode,
        hasBeforeImage: !!data.beforeImage,
        hasAfterImage: !!data.afterImage,
        mobileImageOverlap: data.mobileImageOverlap,
        mobileImageMaxWidth: data.mobileImageMaxWidth,
        mobileImageHeight: data.mobileImageHeight
    });
    
    const deleteLogo = '/img/delete.svg';
    const defaultBeforeImage = '/img/photo_card_2.jpg'; // Before Fallback - existiert
    const defaultAfterImage = '/img/photo_card_1.jpg'; // After Fallback - existiert
    const fonts = useFont();
    
    // Inline Editing States
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    
    // States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const widgetRef = useRef(null);
    const containerRef = useRef(null);

    // Mobile Layout Detection (750px Breakpoint)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Responsive Widget Styles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        '--imageOverlap': `${data.imageOverlap || 25}px`
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 400)
        }),
        // Fallback für alte Padding-Property (Backwards Compatibility)
        ...(layoutStyles.paddingTop === '0px' && layoutStyles.paddingBottom === '0px' && {
            padding: appendPx(data.padding || 60)
        })
    };

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // Widget Click Handler
    const handleWidgetClick = (event) => {
        console.log('🔍 ComparisonWidget clicked:', { 
            isEditMode, 
            hasOnSelectWidget: !!onSelectWidget, 
            widgetId: data.id,
            target: event.target.tagName
        });
        
        if (isEditMode && onSelectWidget && !isInlineEditing) {
            event.stopPropagation();
            onSelectWidget(data.id);
        }
    };

    // Smart Outside-Click Handler für Inline Editing
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // Inline Editing Functions (nur für Titel und Labels)
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        if (!onUpdate || !fieldName || fieldName !== isInlineEditing) return;
        
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleInlineSubmit(fieldName);
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Render inline editable text (nur für einfache Texte)
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={style}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: '-11px',
                    right: '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        ✓
                    </button>
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        ×
                    </button>
                </div>
            );
            
            return (
                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            color: '#333333',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight
                        }}
                    />
                    {renderActionButtons()}
                </div>
            );
        }

        // Normal display with click handler
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleInlineEdit(fieldName, currentValue);
        };

        return React.createElement(Element, {
            style: {
                ...style,
                cursor: isEditMode ? 'text' : 'default',
                transition: 'all 0.3s ease'
            },
            onClick: isEditMode ? handleElementClick : undefined,
            onMouseEnter: isEditMode ? (e) => {
                e.target.style.background = 'linear-gradient(45deg, rgba(255, 255, 255, 0.02) 0%, rgba(192, 192, 192, 0.04) 25%, rgba(255, 255, 255, 0.06) 50%, rgba(192, 192, 192, 0.04) 75%, rgba(255, 255, 255, 0.02) 100%)';
            } : undefined,
            onMouseLeave: isEditMode ? (e) => {
                e.target.style.background = 'transparent';
            } : undefined
        }, currentValue || placeholder);
    };

    // Layout Configuration (wie SpiritualTestimonial)
    const getLayoutConfig = () => {
        const layoutType = data.comparisonLayout || 'text-left-images-right';
        
        const layouts = {
            'text-left-images-right': {
                textPosition: 'left',
                imagePosition: 'right',
                direction: 'row',
                textAlignment: 'left',
                imageAlignment: 'center'
            },
            'text-right-images-left': {
                textPosition: 'right',
                imagePosition: 'left',
                direction: 'row-reverse',
                textAlignment: 'left',
                imageAlignment: 'center'
            },
            'text-top-images-bottom': {
                textPosition: 'top',
                imagePosition: 'bottom',
                direction: 'column',
                textAlignment: 'center',
                imageAlignment: 'center'
            },
            'images-top-text-bottom': {
                textPosition: 'bottom',
                imagePosition: 'top',
                direction: 'column-reverse',
                textAlignment: 'center',
                imageAlignment: 'center'
            },
            'side-by-side-minimal': {
                textPosition: 'bottom',
                imagePosition: 'top',
                direction: 'column',
                textAlignment: 'center',
                imageAlignment: 'center',
                imageStyle: 'side-by-side'
            }
        };
        
        return layouts[layoutType] || layouts['text-left-images-right'];
    };

    const layoutConfig = getLayoutConfig();

    // Layout Helper Functions
    const getMainLayoutStyle = () => {
        
        if (isMobileLayout) {
            return {
                display: 'flex',
                flexDirection: 'column',
                gap: appendPx(data.contentSpacing || 30),
                alignItems: 'center',
                maxWidth: appendPx(data.maxWidth || 1200),
                margin: '0 auto',
                // Content Spacing - verhindert dass Inhalt an den Rändern klebt
                padding: `0 ${data.contentSpacingMobile || 20}px`
            };
        }
        
        // Verwende layoutConfig statt data direkt
        const direction = isMobileLayout ? 'column' : layoutConfig.direction;
        const alignment = layoutConfig.imageAlignment === 'top' ? 'flex-start' : 
                         layoutConfig.imageAlignment === 'bottom' ? 'flex-end' : 'center';
        
        return {
            display: 'flex',
            flexDirection: direction,
            gap: appendPx(data.contentSpacing || 60),
            alignItems: alignment,
            maxWidth: appendPx(data.maxWidth || 1200),
            margin: '0 auto',
            // Content Spacing - verhindert dass Inhalt an den Rändern klebt
            padding: `0 ${data.contentSpacing || 40}px`
        };
    };

    // Image Container mit Overlap-Effekt
    const renderImageContainer = () => {
        const isSideBySide = data.comparisonLayout === 'side-by-side-minimal';
        
        if (isSideBySide) {
            // Side by Side Layout - keine Überlappung
            return (
                <div style={{
                    display: 'flex',
                    gap: appendPx(data.imageSpacing || 20),
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    {/* Before Image */}
                    <div style={{ position: 'relative' }}>
                        <img
                            src={data.beforeImage || defaultBeforeImage}
                            alt={data.beforeLabel || 'Before'}
                            style={{
                                width: appendPx(isMobileLayout ? 
                                    Math.min(data.beforeImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                    (data.beforeImageWidth || data.imageWidth || 240)),
                                height: appendPx(isMobileLayout ? 
                                    (data.mobileImageHeight || 320) : 
                                    (data.beforeImageHeight || data.imageHeight || 300)),
                                minWidth: appendPx(isMobileLayout ? 
                                    Math.min(data.beforeImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                    (data.beforeImageWidth || data.imageWidth || 240)),
                                minHeight: appendPx(isMobileLayout ? 
                                    (data.mobileImageHeight || 320) : 
                                    (data.beforeImageHeight || data.imageHeight || 300)),
                                maxWidth: appendPx(isMobileLayout ? 
                                    Math.min(data.beforeImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                    (data.beforeImageWidth || data.imageWidth || 240)),
                                maxHeight: appendPx(isMobileLayout ? 
                                    (data.mobileImageHeight || 320) : 
                                    (data.beforeImageHeight || data.imageHeight || 300)),
                                borderRadius: appendPx(data.beforeImageBorderRadius || data.imageBorderRadius || 12),
                                boxShadow: (data.beforeImageShadow !== undefined ? data.beforeImageShadow : data.imageShadow) ? '0 8px 25px rgba(0, 0, 0, 0.15)' : 'none',
                                objectFit: 'cover',
                                transition: 'all 0.3s ease'
                            }}
                            className="before-image"
                            onError={(e) => {
                                console.log('Before image error, using fallback');
                                if (e.target.src !== defaultBeforeImage) {
                                    e.target.src = defaultBeforeImage;
                                } else {
                                    // Letzter Fallback: Platzhalter
                                    e.target.style.display = 'none';
                                    e.target.parentNode.innerHTML = `
                                        <div style="
                                            width: ${isMobileLayout ? 
                                                Math.min(data.beforeImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                                (data.beforeImageWidth || data.imageWidth || 240)}px;
                                            height: ${isMobileLayout ? 
                                                (data.mobileImageHeight || 320) : 
                                                (data.beforeImageHeight || data.imageHeight || 300)}px;
                                            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                                            border-radius: ${data.imageBorderRadius || 12}px;
                                            display: flex;
                                            align-items: center;
                                            justify-content: center;
                                            color: #666;
                                            font-size: 16px;
                                            font-weight: 500;
                                        ">
                                            Before Image
                                        </div>
                                    `;
                                }
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            backgroundColor: data.imageLabelBackgroundColor || 'rgba(255, 255, 255, 0.9)',
                            color: data.imageLabelColor || '#666666',
                            padding: appendPx(data.imageLabelPadding || 8),
                            borderRadius: appendPx(data.imageLabelBorderRadius || 6),
                            fontSize: appendPx(data.imageLabelSize || 14),
                            fontWeight: data.imageLabelWeight || '600',
                            fontFamily: data.imageLabelFont || fonts.globalSmallFont,
                            backdropFilter: 'blur(4px)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}>
                            {data.showBeforeDate !== false ? (data.beforeDate || 'Before') : 'Before'}
                        </div>
                    </div>

                    {/* After Image */}
                    <div style={{ position: 'relative' }}>
                        <img
                            src={data.afterImage || defaultAfterImage}
                            alt={data.afterLabel || 'After'}
                            style={{
                                width: appendPx(isMobileLayout ? 
                                    Math.min(data.afterImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                    (data.afterImageWidth || data.imageWidth || 240)),
                                height: appendPx(isMobileLayout ? 
                                    (data.mobileImageHeight || 320) : 
                                    (data.afterImageHeight || data.imageHeight || 300)),
                                minWidth: appendPx(isMobileLayout ? 
                                    Math.min(data.afterImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                    (data.afterImageWidth || data.imageWidth || 240)),
                                minHeight: appendPx(isMobileLayout ? 
                                    (data.mobileImageHeight || 320) : 
                                    (data.afterImageHeight || data.imageHeight || 300)),
                                maxWidth: appendPx(isMobileLayout ? 
                                    Math.min(data.afterImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                    (data.afterImageWidth || data.imageWidth || 240)),
                                maxHeight: appendPx(isMobileLayout ? 
                                    (data.mobileImageHeight || 320) : 
                                    (data.afterImageHeight || data.imageHeight || 300)),
                                borderRadius: appendPx(data.afterImageBorderRadius || data.imageBorderRadius || 12),
                                boxShadow: (data.afterImageShadow !== undefined ? data.afterImageShadow : data.imageShadow) ? '0 8px 25px rgba(0, 0, 0, 0.15)' : 'none',
                                objectFit: 'cover',
                                transition: 'all 0.3s ease'
                            }}
                            className="after-image"
                            onError={(e) => {
                                console.log('After image error, using fallback');
                                if (e.target.src !== defaultAfterImage) {
                                    e.target.src = defaultAfterImage;
                                } else {
                                    // Letzter Fallback: Platzhalter
                                    e.target.style.display = 'none';
                                    e.target.parentNode.innerHTML = `
                                        <div style="
                                            width: ${isMobileLayout ? 
                                                Math.min(data.afterImageWidth || data.imageWidth || 240, data.mobileImageMaxWidth || 280) : 
                                                (data.afterImageWidth || data.imageWidth || 240)}px;
                                            height: ${isMobileLayout ? 
                                                (data.mobileImageHeight || 320) : 
                                                (data.afterImageHeight || data.imageHeight || 300)}px;
                                            background: linear-gradient(135deg, #c3cfe2 0%, #f5f7fa 100%);
                                            border-radius: ${data.imageBorderRadius || 12}px;
                                            display: flex;
                                            align-items: center;
                                            justify-content: center;
                                            color: #666;
                                            font-size: 16px;
                                            font-weight: 500;
                                        ">
                                            After Image
                                        </div>
                                    `;
                                }
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            backgroundColor: data.imageLabelBackgroundColor || 'rgba(255, 255, 255, 0.9)',
                            color: data.imageLabelColor || '#666666',
                            padding: appendPx(data.imageLabelPadding || 8),
                            borderRadius: appendPx(data.imageLabelBorderRadius || 6),
                            fontSize: appendPx(data.imageLabelSize || 14),
                            fontWeight: data.imageLabelWeight || '600',
                            fontFamily: data.imageLabelFont || fonts.globalSmallFont,
                            backdropFilter: 'blur(4px)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}>
                            {data.showAfterDate !== false ? (data.afterDate || 'After') : 'After'}
                        </div>
                    </div>
                </div>
            );
        }
        
        // Overlapped Layout (Standard)
        // Verwende separate Overlap-Werte für Desktop und Mobile
        const overlap = isMobileLayout ? 
            (data.mobileImageOverlap !== undefined ? data.mobileImageOverlap : data.imageOverlap || 25) : 
            (data.imageOverlap || 25);
            
        // Verwende mobile-spezifische Dimensionen wenn im Mobile Layout
        let beforeWidth, afterWidth, beforeHeight, afterHeight;
        
        if (isMobileLayout) {
            // Mobile: verwende mobile-spezifische Werte oder fallback zu Desktop-Werten
            const mobileMaxWidth = data.mobileImageMaxWidth || 280;
            const mobileHeight = data.mobileImageHeight || 320;
            
            beforeWidth = Math.min(data.beforeImageWidth || data.imageWidth || 240, mobileMaxWidth);
            afterWidth = Math.min(data.afterImageWidth || data.imageWidth || 240, mobileMaxWidth);
            beforeHeight = mobileHeight;
            afterHeight = mobileHeight;
        } else {
            // Desktop: verwende normale Werte
            beforeWidth = data.beforeImageWidth || data.imageWidth || 240;
            afterWidth = data.afterImageWidth || data.imageWidth || 240;
            beforeHeight = data.beforeImageHeight || data.imageHeight || 300;
            afterHeight = data.afterImageHeight || data.imageHeight || 300;
        }
        
        // Container muss groß genug für beide Bilder + Overlap sein
        // Mobile: vertikal gestapelt + leichte horizontale Verschiebung, Desktop: diagonal überlappend
        const mobileHorizontalOffset = Math.min(overlap * 0.3, 50);
        const containerWidth = isMobileLayout ? 
            Math.max(beforeWidth, afterWidth) + mobileHorizontalOffset : 
            Math.max(beforeWidth, afterWidth) + overlap;
        const containerHeight = isMobileLayout ? 
            Math.max(beforeHeight, afterHeight) + overlap : 
            Math.max(beforeHeight, afterHeight) + overlap;
        
        const imageContainerStyle = {
            position: 'relative',
            width: appendPx(containerWidth),
            height: appendPx(containerHeight),
            flexShrink: 0
        };

        const beforeImageStyle = {
            position: 'absolute',
            top: '0',
            left: '0',
            width: appendPx(beforeWidth),
            height: appendPx(beforeHeight),
            minWidth: appendPx(beforeWidth),
            minHeight: appendPx(beforeHeight),
            maxWidth: appendPx(beforeWidth),
            maxHeight: appendPx(beforeHeight),
            borderRadius: appendPx(data.beforeImageBorderRadius || data.imageBorderRadius || 12),
            boxShadow: (data.beforeImageShadow !== undefined ? data.beforeImageShadow : data.imageShadow) ? '0 8px 25px rgba(0, 0, 0, 0.15)' : 'none',
            objectFit: 'cover',
            zIndex: 1,
            transition: 'all 0.3s ease'
        };

        const afterImageStyle = {
            position: 'absolute',
            // Mobile: hauptsächlich vertikal nach unten + leicht seitlich, Desktop: diagonal
            top: appendPx(overlap),
            left: appendPx(isMobileLayout ? Math.min(overlap * 0.3, 50) : overlap),
            width: appendPx(afterWidth),
            height: appendPx(afterHeight),
            minWidth: appendPx(afterWidth),
            minHeight: appendPx(afterHeight),
            maxWidth: appendPx(afterWidth),
            maxHeight: appendPx(afterHeight),
            borderRadius: appendPx(data.afterImageBorderRadius || data.imageBorderRadius || 12),
            boxShadow: (data.afterImageShadow !== undefined ? data.afterImageShadow : data.imageShadow) ? '0 12px 35px rgba(0, 0, 0, 0.2)' : 'none',
            objectFit: 'cover',
            zIndex: 2,
            transition: 'all 0.3s ease'
        };

        const labelStyle = {
            position: 'absolute',
            top: '8px',
            left: '8px',
            backgroundColor: data.imageLabelBackgroundColor || 'rgba(255, 255, 255, 0.9)',
            color: data.imageLabelColor || '#666666',
            padding: appendPx(data.imageLabelPadding || 8),
            borderRadius: appendPx(data.imageLabelBorderRadius || 6),
            fontSize: appendPx(data.imageLabelSize || 14),
            fontWeight: data.imageLabelWeight || '600',
            fontFamily: data.imageLabelFont || fonts.globalSmallFont,
            zIndex: 3,
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        };

        return (
            <div style={imageContainerStyle}>
                {/* Before Image */}
                <div style={{ position: 'relative' }}>
                    <img
                        src={data.beforeImage || defaultBeforeImage}
                        alt={data.beforeLabel || 'Before'}
                        style={beforeImageStyle}
                        onError={(e) => {
                            console.log('Before image error (overlap), using fallback');
                            if (e.target.src !== defaultBeforeImage) {
                                e.target.src = defaultBeforeImage;
                            } else {
                                // Letzter Fallback: Platzhalter
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = `
                                    <div style="
                                        position: absolute;
                                        top: 0;
                                        left: 0;
                                        width: ${beforeWidth}px;
                                        height: ${beforeHeight}px;
                                        background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                                        border-radius: ${data.imageBorderRadius || 12}px;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        color: #666;
                                        font-size: 16px;
                                        font-weight: 500;
                                        z-index: 1;
                                    ">
                                        Before Image
                                    </div>
                                `;
                            }
                        }}
                    />
                    {/* Before Label */}
                    <div style={labelStyle}>
                        {data.showBeforeDate !== false && renderEditableText(
                            'beforeDate',
                            data.beforeDate,
                            'span',
                            { 
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                fontFamily: 'inherit',
                                color: 'inherit',
                                background: 'transparent',
                                border: 'none',
                                padding: '0',
                                margin: '0'
                            },
                            'Before Date'
                        )}
                    </div>
                </div>

                {/* After Image */}
                <div style={{ position: 'relative' }}>
                    <img
                        src={data.afterImage || defaultAfterImage}
                        alt={data.afterLabel || 'After'}
                        style={afterImageStyle}
                        onError={(e) => {
                            console.log('After image error (overlap), using fallback');
                            if (e.target.src !== defaultAfterImage) {
                                e.target.src = defaultAfterImage;
                            } else {
                                // Letzter Fallback: Platzhalter
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = `
                                    <div style="
                                        position: absolute;
                                        top: ${overlap}px;
                                        left: ${isMobileLayout ? Math.min(overlap * 0.3, 50) : overlap}px;
                                        width: ${afterWidth}px;
                                        height: ${afterHeight}px;
                                        background: linear-gradient(135deg, #c3cfe2 0%, #f5f7fa 100%);
                                        border-radius: ${data.imageBorderRadius || 12}px;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        color: #666;
                                        font-size: 16px;
                                        font-weight: 500;
                                        z-index: 2;
                                    ">
                                        After Image
                                    </div>
                                `;
                            }
                        }}
                    />
                    {/* After Label */}
                    <div style={{
                        ...labelStyle,
                        // Mobile: vertikal + leicht seitlich, Desktop: diagonal versetzt
                        top: appendPx(8 + overlap),
                        left: appendPx(8 + (isMobileLayout ? Math.min(overlap * 0.3, 50) : overlap))
                    }}>
                        {data.showAfterDate !== false && renderEditableText(
                            'afterDate',
                            data.afterDate,
                            'span',
                            { 
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                fontFamily: 'inherit',
                                color: 'inherit',
                                background: 'transparent',
                                border: 'none',
                                padding: '0',
                                margin: '0'
                            },
                            'After Date'
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Text Container
    const renderTextContainer = () => {
        const textContainerStyle = {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: appendPx(data.contentSpacing * 0.5 || 30),
            textAlign: data.textAlignment || 'left',
            minWidth: 0
        };

        return (
            <div style={textContainerStyle}>
                {/* Title - Inline Editable - Mit Show/Hide Toggle */}
                {data.showTitleText !== false && renderEditableText(
                    'titleText',
                    data.titleText,
                    'h2',
                    {
                        // Title Kombinierte Effects
                        ...((!data.titleEffect || data.titleEffect === 'none') && {
                            color: data.titleColor || '#000000'
                        }),
                        ...getTextEffectStyles(data.titleEffect),
                        
                        fontSize: appendPx(data.titleSize || 42),
                        fontWeight: data.titleWeight || '700',
                        fontFamily: data.titleFont || fonts.globalBigFont,
                        margin: '0',
                        lineHeight: '1.2',
                        textAlign: data.comparisonLayout === 'text-top-images-bottom' ? 'center' : data.textAlignment,
                        // Responsive Font-Sizes
                        '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 42),
                        '--normal-size': appendPx(data.titleSize || 42),
                        '--big-size': appendPx(data.titleSizeBig || data.titleSize || 42)
                    },
                    'Your Transformation Journey'
                )}

                {/* Rich Text Content - Mit Show/Hide Toggle */}
                {data.showRichTextContent !== false && (
                    <div 
                        style={{
                            fontSize: appendPx(data.richTextSize || 16),
                            fontWeight: data.richTextWeight || '400',
                            fontFamily: data.richTextFont || fonts.globalSmallFont,
                            color: data.richTextColor || '#333333',
                            lineHeight: data.richTextLineHeight || 1.6,
                            margin: '0'
                        }}
                        dangerouslySetInnerHTML={{ 
                            __html: data.richTextContent || '<p>Describe your transformation journey here...</p>' 
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <>
            <div 
                className="comparison-widget"
                style={widgetStyle}
                ref={containerRef}
                onClick={handleWidgetClick}
            >
                {/* Delete Button */}
                <div>
                    {isEditMode && (
                        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                            <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                        </button>
                    )}
                    
                    {/* 🤖 AI REFRESH ICON */}
                    {isEditMode && (
                        <AIRefreshIcon 
                            widget={{
                                ...data,
                                type: 'ComparisonWidget'
                            }}
                            spaceId={null}
                            onContentRefreshed={(refreshedWidget) => {
                                console.log('🤖 [COMPARISONWIDGET] AI-refreshed content received:', refreshedWidget);
                                onUpdate(refreshedWidget);
                            }}
                            position="top-left"
                        />
                    )}
                    
                    {isEditMode && showDeleteModal && (
                        <DeleteModal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            onConfirm={(event) => handleDelete(event, data.id)}
                        />
                    )}
                </div>

                {/* Main Content Container */}
                <div style={getMainLayoutStyle()}>
                    {/* Text Container */}
                    <div className="text-container">
                        {renderTextContainer()}
                    </div>
                    
                    {/* Images Container */}
                    <div className="image-container">
                        {renderImageContainer()}
                    </div>
                </div>

                {/* Fallback wenn keine Bilder */}
                {(!data.beforeImage && !data.afterImage) && isEditMode && (
                    <div style={{
                        padding: '40px',
                        border: '2px dashed #dee2e6',
                        borderRadius: '12px',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        textAlign: 'center',
                        marginTop: '20px'
                    }}>
                        <p style={{ margin: 0, fontSize: '16px' }}>
                            Upload Before & After images via the Edit Panel
                        </p>
                    </div>
                )}
            </div>
        </>
    );
}

export default ComparisonWidget;
